import {
	Box,
	Stack,
	Button,
	Icons,
	Inline,
	Table,
	joinAttributes,
	Time,
	TimeDuration,
	Tooltip,
	SelectInput,
	Container,
	Text,
	Money,
	MoneySum,
	Alert,
	Tabs,
	TabContent,
	TabsList,
	TabItem,
	Spinner,
} from "@sembark-travel/ui/base"
import { Link, useLocationQuery } from "@sembark-travel/ui/router"
import {
	Search,
	TSearchParams,
	useSearch,
	areAdvancedFiltersAppliedDefault,
	ListView,
} from "@sembark-travel/ui/list"
import {
	dateToQuery,
	dateToUTCString,
	parseDateFromQuery,
	startOf,
	endOf,
	subtractUnit,
	formatDate,
	addUnit,
	isAfter,
	localOrUtcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"
import { IListResponse, XHRInstance, useXHR } from "@sembark-travel/xhr"
import { showSnackbar } from "@sembark-travel/ui/snackbar"
import React, { useEffect, useMemo } from "react"
import useSWR from "swr"
import { $PropertyType } from "utility-types"
import { Email, PhoneNumber } from "../Contacts"
import { generatePath } from "../router-utils"
import { SelectTripDestination } from "../TripDestinations"
import { TTripDestination } from "../TripDestinations"
import { SelectTripOwners } from "../Trips"
import { SelectTripSources } from "../TripSources"
import { ITripSource } from "../TripSources"
import { IUser } from "../Users"
import { TTripCheckInOutReport } from "./types"
import {
	SelectField,
	SwitchInputField,
	isTruthy,
} from "@sembark-travel/ui/form"
import { TFormattedMoney } from "@sembark-travel/money"
import { DownloadManager } from "../DownloadManager"

type TMeta = {
	total_checkouts: number
	total_package_money: Array<TFormattedMoney>
	total_bookings_money: Array<TFormattedMoney>
}
function XHR(xhr: XHRInstance) {
	return {
		async get(params?: unknown) {
			return xhr
				.get<
					IListResponse<TTripCheckInOutReport> & {
						meta: TMeta
					}
				>("/trip-check-in-out-reports", { params })
				.then(({ data }) => data)
		},
	}
}

function StatsProvider({
	params,
	children,
}: {
	params: TFilters
	children: (data: {
		stats: TMeta | undefined
		loading: boolean
	}) => React.ReactNode
}) {
	const xhr = useXHR()

	const statsParams = useMemo(() => {
		return {
			q: params.q || null,
			after: dateToUTCString(params.after),
			before: dateToUTCString(params.before),
			destinations: (params.destinations || []).map((d) => d.id),
			sources: (params.trip_sources || []).map((d) => d.id),
			sales_team: (params.sales_team || []).map((d) => d.id),
			include_incompleted: isTruthy(params.include_incompleted) ? 1 : 0,
			report_type: params.reportType || "checkout",
		}
	}, [
		params.q,
		params.after,
		params.before,
		params.destinations,
		params.trip_sources,
		params.sales_team,
		params.include_incompleted,
		params.reportType,
	])

	const {
		data: stats,
		error,
		isValidating,
	} = useSWR(
		`/trip-check-in-out-reports/stats-${JSON.stringify(statsParams)}`,
		async () => {
			return xhr
				.get<{
					data: TMeta
				}>("/trip-check-in-out-reports/stats", { params: statsParams })
				.then(({ data }) => data.data)
		},
		{
			onError: () => {
				showSnackbar("Failed to load stats. Please try again.", {
					icon: <Icons.AttentionSolid color="danger" />,
					duration: 5000,
				})
			},
		}
	)

	const loading = (!stats && !error) || isValidating

	return <>{children({ stats, loading })}</>
}

type TFilters = TSearchParams & {
	after: Date
	before: Date
	destinations?: Array<TTripDestination>
	trip_sources?: Array<ITripSource>
	sales_team?: Array<IUser>
	intervalType: "month" | "week" | "day"
	include_incompleted?: 0 | 1
	reportType: "checkin" | "checkout"
}

type TFiltersInQuery = TSearchParams & {
	after?: string
	before?: string
	destinations?: Array<string>
	trip_sources?: Array<string>
	sales_team?: Array<string>
	intervalType: "month" | "week" | "day"
	include_incompleted?: 0 | 1
	reportType: "checkin" | "checkout"
}

function filtersToQuery(filters: TFilters): TFiltersInQuery {
	const {
		q,
		after,
		before,
		intervalType,
		page,
		destinations,
		trip_sources,
		sales_team,
		include_incompleted,
		reportType,
	} = filters
	const query: TFiltersInQuery = {
		after: dateToQuery(after || startOf(new Date(), "month")),
		before: dateToQuery(before || endOf(new Date(), "month")),
		intervalType: intervalType || "month",
		reportType: reportType || "checkout",
		include_incompleted: isTruthy(include_incompleted) ? 1 : undefined,
	}
	if (q) {
		query.q = q
	}
	if (page) query.page = page
	if (destinations) {
		query.destinations = destinations.map((d) => `${d.id}_${d.name}`)
	}
	if (trip_sources) {
		query.trip_sources = trip_sources.map((d) => `${d.id}_${d.name}`)
	}
	if (sales_team) {
		query.sales_team = sales_team.map((d) => `${d.id}_${d.name}`)
	}
	return query
}

function queryToFilters(query: TFiltersInQuery): TFilters {
	const {
		q,
		after,
		before,
		intervalType,
		page,
		destinations,
		trip_sources,
		sales_team,
		include_incompleted,
		reportType,
	} = query
	const filters: TFilters = {
		after: after
			? startOf(parseDateFromQuery(after), "day")
			: startOf(new Date(), intervalType || "month"),
		before: before
			? endOf(parseDateFromQuery(before), "day")
			: endOf(new Date(), intervalType || "month"),
		intervalType: intervalType || "month",
		reportType: reportType || "checkout",
		include_incompleted: isTruthy(include_incompleted) ? 1 : 0,
	}
	if (q) {
		filters.q = q
	}
	if (page) filters.page = page
	if (destinations) {
		filters.destinations = destinations.map((d) => {
			const [id, ...name] = d.split("_")
			return {
				id,
				name: name.join("_"),
			}
		}) as unknown as $PropertyType<TFilters, "destinations">
	}
	if (trip_sources) {
		filters.trip_sources = trip_sources.map((d) => {
			const [id, ...name] = d.split("_")
			return {
				id,
				name: name.join("_"),
			}
		}) as unknown as $PropertyType<TFilters, "trip_sources">
	}
	if (sales_team) {
		filters.sales_team = sales_team.map((d) => {
			const [id, ...name] = d.split("_")
			return {
				id,
				name: name.join("_"),
			}
		}) as unknown as $PropertyType<TFilters, "sales_team">
	}
	return filters
}

export function TripCheckInOutReportsList() {
	const [query, setQuery] = useLocationQuery({
		toQuery: filtersToQuery,
		fromQuery: queryToFilters,
	})
	const [params, setParams] = useSearch<TFilters>(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])

	return (
		<Box>
			<Search
				title="Trip Check In/Out Report"
				initialParams={params}
				onSearch={setParams}
				Filters={Filters}
				resetParams={(params) => ({
					q: "",
					after: params.after,
					before: params.before,
					intervalType: params.intervalType,
					reportType: params.reportType || "checkout",
				})}
				areAdvancedFiltersApplied={(params) => {
					const {
						before,
						after,
						intervalType,
						include_incompleted,
						reportType,
						...others
					} = params
					return (
						isTruthy(include_incompleted) ||
						areAdvancedFiltersAppliedDefault(others)
					)
				}}
				actions={({ setSearchParams, searchParams }) => (
					<>
						<Inline alignItems="center" gap="2">
							<Button
								onClick={() => {
									const after = startOf(
										subtractUnit(
											searchParams.after,
											1,
											searchParams.intervalType
										),
										searchParams.intervalType
									)
									setSearchParams({
										after,
										before: endOf(after, searchParams.intervalType),
									})
								}}
							>
								<Icons.ChevronDown rotate="90" />
							</Button>
							<Box>
								<SelectInput
									value={searchParams.intervalType}
									onChange={(e) => {
										const type = (e.currentTarget.value ||
											"month") as typeof searchParams.intervalType
										setSearchParams({
											intervalType: type,
											after: startOf(new Date(), type),
											before: endOf(new Date(), type),
										})
									}}
								>
									<option value="month">Month</option>
									<option value="week">Week</option>
									<option value="day">Day</option>
								</SelectInput>
							</Box>
							<Button
								onClick={() => {
									const after = startOf(
										addUnit(searchParams.after, 1, searchParams.intervalType),
										searchParams.intervalType
									)
									setSearchParams({
										after,
										before: endOf(after, searchParams.intervalType),
										include_incompleted: isAfter(
											searchParams.before,
											new Date()
										)
											? 1
											: searchParams.include_incompleted,
									})
								}}
							>
								<Icons.ChevronDown rotate="270" />
							</Button>
						</Inline>
					</>
				)}
			>
				{({ searchParams, setSearchParamValue, setSearchParams }) => (
					<Tabs>
						<TabsList>
							<TabItem
								active={searchParams.reportType === "checkout"}
								onClick={() => setSearchParamValue("reportType", "checkout")}
							>
								Check Outs
							</TabItem>
							<TabItem
								active={searchParams.reportType === "checkin"}
								onClick={() => {
									setSearchParams({
										reportType: "checkin",
										include_incompleted: 1,
									})
								}}
							>
								Check Ins
							</TabItem>
						</TabsList>
						<TabContent>
							<Stack gap="4" paddingY="4">
								<Container display="flex" flexDirection="row" gap="2">
									<Text
										fontWeight="semibold"
										fontSize="xl"
										title={`${formatDate(params.after, "ddd D MMM, YYYY")} - ${formatDate(params.before, "ddd D MMM, YYYY")}`}
									>
										{formatDate(params.after, "ddd D MMM")} -{" "}
										{formatDate(params.before, "ddd D MMM")}
									</Text>
								</Container>
								<Container>
									<StatsProvider params={params}>
										{({ stats, loading: statsLoading }) => (
											<Inline
												bgColor="default"
												roundedRight="lg"
												borderLeftWidth="4"
												borderColor="accent"
												justifyContent="between"
												alignItems="stretch"
												collapseBelow="md"
												gap="4"
												position="relative"
											>
												<Inline alignItems="stretch" collapseBelow="md">
													<Box paddingX="8" paddingY="4">
														<Box fontWeight="semibold" color="muted">
															Total{" "}
															{searchParams.reportType === "checkin"
																? "Check-Ins"
																: "Check-Outs"}
														</Box>
														<Box fontWeight="semibold" fontSize="3xl">
															{stats?.total_checkouts ?? 0}
														</Box>
													</Box>
													<Inline alignItems="stretch" flexWrap="wrap">
														<Box paddingX="8" paddingY="4">
															<Box fontWeight="semibold" color="muted">
																Total Packages
															</Box>
															<Box fontWeight="semibold" fontSize="3xl">
																<MoneySum
																	money={stats?.total_package_money ?? []}
																	roundOff
																/>
															</Box>
														</Box>
														<Box paddingX="8" paddingY="4">
															<Box fontWeight="semibold" color="muted">
																Total Bookings
															</Box>
															<Box fontWeight="semibold" fontSize="3xl">
																<MoneySum
																	money={stats?.total_bookings_money ?? []}
																	roundOff
																/>
															</Box>
														</Box>
													</Inline>
													{statsLoading ? (
														<Stack
															alignItems="center"
															width="full"
															position="absolute"
															justifyContent="center"
															height="full"
															top="0"
															right="0"
															style={{ backgroundColor: "#ffffff9e" }}
														>
															<Spinner />
														</Stack>
													) : null}
												</Inline>
												<Stack padding={"4"} gap="1">
													<Box fontWeight="semibold" textAlign={"right"}>
														{searchParams.reportType === "checkin"
															? "Check-Ins"
															: "Check-Outs"}{" "}
														Report
													</Box>
													<DownloadManager
														mode="customColumnsWithDateRange"
														title={`Export ${searchParams.reportType === "checkin" ? "Check-Ins" : "Check-Outs"} Report`}
														reportType={searchParams.reportType}
														downloadUrl="/trip-check-in-out-reports/download"
														startDate={params.after}
														endDate={params.before}
														getRequestParams={(after, before) => ({
															...filtersToRequestParams(searchParams),
															after: dateToUTCString(startOf(after, "day")),
															before: dateToUTCString(endOf(before, "day")),
														})}
													>
														{({ open }) => {
															return (
																<Button
																	size="sm"
																	level="primary"
																	onClick={() => {
																		open("presets")
																	}}
																>
																	<Icons.DocumentDownload />
																	Download Report
																</Button>
															)
														}}
													</DownloadManager>
												</Stack>
											</Inline>
										)}
									</StatsProvider>
								</Container>
								<Container>
									{isTruthy(params.include_incompleted) ? (
										<Alert status="warning" inline>
											Including data from Incompleted/Future Trips
										</Alert>
									) : null}
								</Container>
								<ListView<TTripCheckInOutReport, TFilters>
									pageKey="trip-check-in-out-reports"
									params={params}
									onPageChange={(page) => setParams({ ...params, page })}
									fetch={(xhr, params) => {
										return XHR(xhr).get(filtersToRequestParams(params))
									}}
								>
									{({ items }) => {
										return (
											<Table
												hover
												responsive
												headers={[
													"Id",
													"Guest",
													"Basic Details",
													"Start",
													"End",
													"Package",
													"Tax (Estm.)",
													"Bookings",
													"Profit (Estm.)",
													"Profit % (Estm.)",
												]}
												bordered
												alignCols={{
													3: "center",
													4: "center",
													5: "right",
													6: "right",
													7: "right",
													8: "right",
													9: "right",
												}}
												rows={items.map((trip) => [
													<Link
														color="accent"
														to={generatePath("/trips/:tripId", {
															tripId: trip.id.toString(),
														})}
													>
														{trip.id}{" "}
														{trip.status === "Dropped" ? (
															<Icons.Ban color="warning" title="Dropped" />
														) : null}
													</Link>,
													trip.tourist ? (
														<Box>
															{trip.tourist.name}
															<PhoneNumber
																value={trip.tourist.phone_numbers}
																iconOnly
																marginLeft="2"
															/>
															<Email
																value={trip.tourist.email}
																iconOnly
																marginLeft="2"
															/>
														</Box>
													) : (
														"N/A"
													),
													<Box>
														{joinAttributes(
															trip.destinations
																.map((d) => d.short_name)
																.join(", "),
															<Box as="span" title={trip.trip_source.name}>
																{trip.trip_source.short_name}
															</Box>,
															<TimeDuration days={trip.days}>
																{trip.days}D
															</TimeDuration>
														)}
													</Box>,
													<Time
														value={localOrUtcTimestampToLocalDate(
															trip.start_date_local,
															trip.start_date
														)}
														format="DD MMM"
														whiteSpace="preserve"
													/>,
													<Time
														value={localOrUtcTimestampToLocalDate(
															trip.end_date_local,
															trip.end_date
														)}
														format="DD MMM"
														whiteSpace="preserve"
													/>,
													<Money
														money={trip.package_price}
														showCurrency
														roundOff
													/>,
													trip.latest_given_quote ? (
														!trip.latest_given_quote.gst_included ? (
															"exc"
														) : trip.estimated_tax_in_cost_currency ? (
															<Money
																money={trip.estimated_tax_in_cost_currency}
																showCurrency
																roundOff
																title={
																	trip.latest_given_quote.tax_percentage
																		? `${trip.latest_given_quote.tax_percentage}%`
																		: "inc"
																}
															/>
														) : (
															"N/A"
														)
													) : null,
													<Link
														to={generatePath(
															"/trips/:tripId/services-bookings",
															{
																tripId: String(trip.id),
															}
														)}
													>
														<Inline
															gap="1"
															alignItems="center"
															justifyContent="end"
														>
															{trip.pending_bookings_count ? (
																<Tooltip content="Some bookings are still pending">
																	<Box>
																		<Icons.AttentionSolid color="warning" />
																	</Box>
																</Tooltip>
															) : null}
															<Box>
																<MoneySum
																	money={trip.total_booked_prices}
																	roundOff
																	separator={
																		<>
																			<br />
																			+&nbsp;
																		</>
																	}
																/>
															</Box>
														</Inline>
													</Link>,
													<Link
														to={generatePath(
															"/trips/:tripId/accounting/transactions",
															{
																tripId: String(trip.id),
															}
														)}
													>
														{trip.estimated_profit_in_cost_currency ? (
															<Money
																money={trip.estimated_profit_in_cost_currency}
																showCurrency
																roundOff
																color={
																	trip.estimated_profit_in_cost_currency
																		.amount <= 0
																		? "danger"
																		: undefined
																}
															/>
														) : (
															<Text
																title={trip.null_profit_reason}
																color="warning"
															>
																N/A
															</Text>
														)}
													</Link>,
													trip.estimated_profit_percentage !== null ? (
														<Percentage
															value={trip.estimated_profit_percentage}
														/>
													) : (
														<Text
															title={trip.null_profit_reason}
															color="warning"
														>
															N/A
														</Text>
													),
												])}
											/>
										)
									}}
								</ListView>
							</Stack>
						</TabContent>
					</Tabs>
				)}
			</Search>
		</Box>
	)
}

function Filters() {
	return (
		<Stack gap="4">
			<SelectField
				select={SelectTripDestination}
				name="destinations"
				label="Destinations"
				multiple
				fetchOnMount
			/>
			<SelectField
				select={SelectTripSources}
				name="trip_sources"
				label="Trip Sources"
				multiple
				fetchOnMount
				withRecentlyDisabledWithinDays={30}
			/>
			<SelectField
				select={SelectTripOwners}
				name="sales_team"
				label="Sales Team"
				multiple
				fetchOnMount
			/>
			<SwitchInputField
				name="include_incompleted"
				label="Include Incompleted/Future Trips"
			/>
		</Stack>
	)
}

function Percentage({
	value,
	fixedPoint = true,
}: {
	value: number
	fixedPoint?: boolean
}) {
	value = Number(value)
	return (
		<Text as="span">
			{fixedPoint ? value.toFixed(2) : value}
			<Box as="span" fontSize="xs" color="muted">
				%
			</Box>
		</Text>
	)
}

function filtersToRequestParams(params: TFilters) {
	return {
		q: params.q || null,
		page: params.page || null,
		after: dateToUTCString(params.after),
		before: dateToUTCString(params.before),
		destinations: (params.destinations || []).map((d) => d.id),
		sources: (params.trip_sources || []).map((d) => d.id),
		sales_team: (params.sales_team || []).map((d) => d.id),
		include_incompleted: isTruthy(params.include_incompleted) ? 1 : 0,
		report_type: params.reportType || "checkout",
	}
}
